/**
 * Opaque-byte persistence layer for the WASM-based Signal library.
 *
 * The new WASM library manages all crypto state internally — identity
 * keys, prekeys, sessions, sender keys all live in Rust heap. Our JS
 * code only needs to SAVE and LOAD that state between browser sessions.
 * This store is a key-value persistence wrapper backed by IndexedDB
 * (with an in-memory LRU cache for fast reads).
 */

// Minimal IndexedDB KV wrapper. Single store, string keys, ArrayBuffer values.
class IdbKv {
  private dbPromise: Promise<IDBDatabase>;

  constructor(dbName: string) {
    this.dbPromise = new Promise((resolve, reject) => {
      // Version 2: added 'kv' store. Version 1 databases that were created
      // before the store existed get it created here via onupgradeneeded.
      const req = indexedDB.open(dbName, 2);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('kv')) {
          db.createObjectStore('kv');
        }
      };
      req.onsuccess = () => {
        resolve(req.result);
      };
      req.onerror = () => {
        reject(req.error);
      };
    });
  }

  async get(key: string): Promise<ArrayBuffer | undefined> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const tx = db.transaction('kv', 'readonly');
      const req = tx.objectStore('kv').get(key);
      req.onsuccess = () => {
        resolve(req.result as ArrayBuffer | undefined);
      };
      req.onerror = () => {
        reject(req.error);
      };
    });
  }

  async put(key: string, value: ArrayBuffer): Promise<void> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const tx = db.transaction('kv', 'readwrite');
      tx.objectStore('kv').put(value, key);
      tx.oncomplete = () => {
        resolve();
      };
      tx.onerror = () => {
        reject(tx.error);
      };
    });
  }

  async delete(key: string): Promise<void> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const tx = db.transaction('kv', 'readwrite');
      tx.objectStore('kv').delete(key);
      tx.oncomplete = () => {
        resolve();
      };
      tx.onerror = () => {
        reject(tx.error);
      };
    });
  }

  // Atomically increment a decimal-string-encoded counter and return the new
  // value. The read and the write happen in ONE readwrite transaction, so
  // concurrent callers (including sibling tabs over the same IDB) are serialized
  // by IndexedDB's transaction model — no get-then-put lost-update race. Used by
  // the DRH-3 state epoch (HIGH-10), where a dropped increment would let a stale
  // tab skip its reload-before-op.
  async increment(key: string): Promise<number> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const tx = db.transaction('kv', 'readwrite');
      const store = tx.objectStore('kv');
      const getReq = store.get(key);
      let next = 1;
      getReq.onsuccess = () => {
        const raw = getReq.result as ArrayBuffer | undefined;
        const cur =
          raw === undefined
            ? 0
            : Number(new TextDecoder().decode(new Uint8Array(raw)));
        next = (Number.isFinite(cur) ? cur : 0) + 1;
        store.put(
          new TextEncoder().encode(String(next)).buffer as ArrayBuffer,
          key,
        );
      };
      tx.oncomplete = () => {
        resolve(next);
      };
      tx.onerror = () => {
        reject(tx.error);
      };
    });
  }

  // Atomically merge one timestamp field into a JSON-encoded
  // {lastSend, lastRecv} record: the read and the write happen in ONE
  // readwrite transaction, so concurrent touches from sibling tabs over the
  // same IDB are serialized and neither update is lost (L-16). Mirrors the
  // atomic `increment` above. Returns the merged record.
  async touchJsonTimestamp(
    key: string,
    field: 'lastSend' | 'lastRecv',
    now: number,
  ): Promise<{ lastSend: number; lastRecv: number }> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const tx = db.transaction('kv', 'readwrite');
      const store = tx.objectStore('kv');
      const getReq = store.get(key);
      const next = { lastSend: 0, lastRecv: 0 };
      getReq.onsuccess = () => {
        const raw = getReq.result as ArrayBuffer | undefined;
        if (raw !== undefined) {
          try {
            const parsed: unknown = JSON.parse(new TextDecoder().decode(raw));
            if (parsed && typeof parsed === 'object') {
              const p = parsed as Record<string, unknown>;
              if (typeof p.lastSend === 'number' && Number.isFinite(p.lastSend))
                next.lastSend = p.lastSend;
              if (typeof p.lastRecv === 'number' && Number.isFinite(p.lastRecv))
                next.lastRecv = p.lastRecv;
            }
          } catch {
            // Corrupt/absent JSON — start from zeros rather than failing.
          }
        }
        next[field] = now;
        store.put(
          new TextEncoder().encode(JSON.stringify(next)).buffer as ArrayBuffer,
          key,
        );
      };
      tx.oncomplete = () => {
        resolve(next);
      };
      tx.onerror = () => {
        reject(tx.error);
      };
    });
  }

  async allKeys(): Promise<string[]> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const tx = db.transaction('kv', 'readonly');
      const req = tx.objectStore('kv').getAllKeys();
      req.onsuccess = () => {
        resolve((req.result ?? []) as string[]);
      };
      req.onerror = () => {
        reject(req.error);
      };
    });
  }
}

// Extract only the viewed bytes from a Uint8Array, avoiding saving the
// entire WASM heap buffer when `bytes.buffer` is a large SharedArrayBuffer.
// Note: `.buffer` may be a SharedArrayBuffer when the WASM heap is shared-
// memory, but `.slice()` always returns a plain ArrayBuffer copy, so the
// returned value is safe to store in IndexedDB (which rejects SABs).
function viewBytes(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  );
}

export interface SessionTimestamp {
  lastSend: number;
  lastRecv: number;
}

export interface SessionKey {
  accountId: string;
  deviceId: number;
}

// MD-h own-sibling backfill outbox entry. `payload` is the plaintext message
// payload (same shape passed to encryptMultiDevice) to be re-encrypted and
// re-delivered to our OWN sibling device that was skipped at send time
// (establish throttled / no usable bundle). A fresh delivery is correct: the
// sibling never received the original, so there is no duplicate to dedupe
// against; the group_key reuse is thread attribution, not dedupe.
//   - recipientAccountId: the peer/thread the original message was sent to
//     (encryptMultiDevice's first arg). Needed to re-encrypt the sibling copy
//     with the right thread attribution.
//   - deviceId: our own sibling device that missed the message.
//   - attempts / notBefore: bounded exponential-backoff retry spacing so a
//     permanently-unreachable sibling does not spin.
//   - groupKey: the logical message's server-minted group_key. Entries are
//     enqueued DURING the fan-out — before the primary send's POST returns —
//     so they are stamped with it afterwards (stampSiblingBackfillGroupKey).
//     A stamped entry's flush POST threads the copy onto the original
//     message; an unstamped one (pre-repair queue) flushes without it, as
//     before.
export interface SiblingBackfillEntry {
  recipientAccountId: string;
  deviceId: number;
  payload: unknown;
  attempts: number;
  notBefore: number;
  createdAt: number;
  groupKey?: string;
}

export class SignalProtocolStore {
  readonly dbName: string;
  private mem = new Map<string, ArrayBuffer | string | number | object>();
  private idb: IdbKv | null;
  // Keys whose IDB write was swallowed (put rejected) and therefore live ONLY in
  // `mem`. clearMemCache must preserve these — wiping them would be permanent
  // data loss (LOW-7). A later successful write for the same key clears it.
  private _failedWrites = new Set<string>();

  constructor(dbName = 'mastodon-e2ee-signal') {
    this.dbName = dbName;
    this.idb = typeof indexedDB !== 'undefined' ? new IdbKv(dbName) : null;
  }

  private cached(key: string): ArrayBuffer | undefined {
    const v = this.mem.get(key);
    if (v instanceof ArrayBuffer) return v;
    return undefined;
  }

  private async loadBytes(key: string): Promise<ArrayBuffer | undefined> {
    const cached = this.cached(key);
    if (cached) return cached;
    if (!this.idb) return undefined;
    const v = await this.idb.get(key);
    if (v !== undefined) this.mem.set(key, v);
    return v;
  }

  private async saveBytes(key: string, bytes: ArrayBuffer): Promise<void> {
    this.mem.set(key, bytes);
    if (this.idb) {
      try {
        await this.idb.put(key, bytes);
        this._failedWrites.delete(key);
      } catch {
        // IDB write failure (e.g. storage cleared mid-session) is non-fatal:
        // the in-memory copy keeps crypto working for the rest of this session.
        // Mark it so clearMemCache never drops this mem-only value (LOW-7).
        this._failedWrites.add(key);
      }
    }
  }

  // L-14: registry of keys whose persisted form is JSON text. loadInternal
  // only attempts a JSON decode for these; every other key — binary Signal
  // records (`identity-record`, `prekey:*`, `signed-prekey:*`, `kyber-prekey:*`,
  // `session:*`, `sender-key:*`, `skdm:*`, `skdm-init:*`) and any unknown key —
  // always returns raw bytes, so a binary record whose bytes happen to be
  // valid JSON text can never be misdecoded into an object.
  //   JSON exact keys: backupCreds, myDeviceId, linked-device, restored-at,
  //     state-epoch, signed-prekey-rotated-at, plaintext-sweep-at,
  //     backup-updated-at, backup-auto-disabled.
  //   JSON prefixes: session-ts:, safety-verified:, remote-id-key:, seq:,
  //     sent:, recv:, replace:, sibling-backfill:, signed-prekey-grace:,
  //     group-dist:, skdm-env:, group-dist-history:, group-dist-owner:,
  //     seen-devices:, identity-ack:.
  private static readonly JSON_EXACT_KEYS: ReadonlySet<string> = new Set([
    'backupCreds',
    'myDeviceId',
    'linked-device',
    'restored-at',
    'state-epoch',
    'signed-prekey-rotated-at',
    'plaintext-sweep-at',
    'backup-updated-at',
    'backup-auto-disabled',
  ]);
  private static readonly JSON_KEY_PREFIXES: readonly string[] = [
    'session-ts:',
    'safety-verified:',
    'remote-id-key:',
    'seq:',
    'sent:',
    'recv:',
    'replace:',
    'sibling-backfill:',
    'signed-prekey-grace:',
    'group-dist:', // receive-side current-distId marker (crypto.ts M-6b)
    'skdm-env:', // SKDM replay/rekey envelope dedupe record (string)
    'group-dist-history:', // M-2/M-6b send-side per-group distId history
    'group-dist-owner:', // M-2 global distId → groupChatId collision map
    'seen-devices:', // H-5 last-seen device-id set per contact
    'identity-ack:', // H-3 one-shot user-acknowledged (contact, device) pins
  ];

  private static isJsonKey(key: string): boolean {
    return (
      SignalProtocolStore.JSON_EXACT_KEYS.has(key) ||
      SignalProtocolStore.JSON_KEY_PREFIXES.some((p) => key.startsWith(p))
    );
  }

  private async loadInternal<T = unknown>(key: string): Promise<T | undefined> {
    const v = this.mem.get(key);
    if (v !== undefined) return v as T;
    if (!this.idb) return undefined;
    let raw: ArrayBuffer | undefined;
    try {
      raw = await this.idb.get(key);
    } catch {
      return undefined;
    }
    if (raw === undefined) return undefined;
    if (SignalProtocolStore.isJsonKey(key)) {
      // Known-JSON key (objects were saved as JSON-encoded ArrayBuffer).
      try {
        const text = new TextDecoder().decode(raw);
        const parsed = JSON.parse(text);
        const decoded = decodeBinaryValues(parsed);
        this.mem.set(key, decoded as ArrayBuffer | object);
        return decoded as T;
      } catch {
        // Corrupt JSON for a known-JSON key — fall through to raw bytes.
      }
    }
    // Binary or unknown key: always raw bytes (L-14).
    this.mem.set(key, raw);
    return raw as unknown as T;
  }

  public async load<T = unknown>(key: string): Promise<T | undefined> {
    return this.loadInternal<T>(key);
  }

  async save(key: string, value: unknown): Promise<void> {
    this.mem.set(key, value as ArrayBuffer | string | number | object);
    if (this.idb) {
      try {
        if (value instanceof ArrayBuffer) {
          await this.idb.put(key, value);
        } else {
          // Encode all non-ArrayBuffer values (objects, strings, numbers, booleans)
          // as JSON so they survive page refresh via IndexedDB. Previously only
          // objects were persisted; primitives (myDeviceId, remote identity keys,
          // rotation timestamps, verification state) were lost on reload (N02).
          const encoded = encodeBinaryValues(value);
          const json = JSON.stringify(encoded);
          const buf = new TextEncoder().encode(json);
          await this.idb.put(key, viewBytes(buf));
        }
        this._failedWrites.delete(key);
      } catch {
        // IDB write failure is non-fatal: in-memory copy persists for this
        // session. Mark it so clearMemCache never drops this mem-only value (LOW-7).
        this._failedWrites.add(key);
      }
    }
  }

  private async removeInternal(key: string): Promise<void> {
    this.mem.delete(key);
    if (this.idb) await this.idb.delete(key);
  }

  public async remove(key: string): Promise<void> {
    await this.removeInternal(key);
  }

  // --- Cross-tab state epoch (DRH-3 single-writer discipline) ---
  // A monotonic counter bumped once per committed mutation of shared ratchet /
  // key state. A sibling tab reads it — the authoritative IndexedDB value,
  // bypassing the in-memory cache — at the start of each writer-locked critical
  // section; if it advanced past the epoch the tab's in-memory WASM client
  // reflects, that client is stale and must be rebuilt from IDB before operating
  // (reload-before-op). Plaintext / sequence caches (sent:/recv:/replace:/seq:)
  // never bump it — they carry no ratchet state.
  private static readonly EPOCH_KEY = 'state-epoch';
  // MD-h: hard cap on the durable own-sibling backfill outbox (oldest dropped).
  static readonly MAX_SIBLING_BACKFILL = 500;
  // M-1: bounded retention for the local plaintext caches (sent:/recv:/
  // replace:) and the sibling-backfill outbox. Entries are stamped with a
  // store-time timestamp (wrapPlaintext below); a lazy sweep — at most once
  // per PLAINTEXT_SWEEP_INTERVAL_MS, tracked by a persisted marker — evicts
  // entries provably older than PLAINTEXT_TTL_MS and enforces a global LRU
  // cap (oldest store-time first) across the three plaintext prefixes. The
  // sweep is also exposed via sweepPlaintextCaches() for the crypto cleanup
  // pass. Entries whose age is unprovable (pre-retention legacy payloads
  // without a stamp) are never TTL-evicted, but count as oldest under the cap.
  static readonly PLAINTEXT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  static readonly MAX_PLAINTEXT_ENTRIES = 5000; // across sent:/recv:/replace:
  static readonly PLAINTEXT_SWEEP_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
  private static readonly SWEEP_MARKER_KEY = 'plaintext-sweep-at';

  async getEpoch(): Promise<number> {
    if (this.idb) {
      try {
        const raw = await this.idb.get(SignalProtocolStore.EPOCH_KEY);
        const n =
          raw === undefined
            ? 0
            : Number(new TextDecoder().decode(new Uint8Array(raw)));
        const val = Number.isFinite(n) ? n : 0;
        this.mem.set(SignalProtocolStore.EPOCH_KEY, val);
        return val;
      } catch {
        // Fall through to the in-memory value on an IDB read failure.
      }
    }
    const m = this.mem.get(SignalProtocolStore.EPOCH_KEY);
    return typeof m === 'number' ? m : 0;
  }

  async bumpEpoch(): Promise<number> {
    if (this.idb) {
      try {
        // Atomic read-modify-write in a single IDB transaction so concurrent
        // bumps (locked γ vs. an out-of-band prekey-heal, incl. across tabs)
        // never coalesce into one increment (HIGH-10).
        const next = await this.idb.increment(SignalProtocolStore.EPOCH_KEY);
        this.mem.set(SignalProtocolStore.EPOCH_KEY, next);
        return next;
      } catch {
        // Fall through to a mem-only advance on an IDB failure.
      }
    }
    const cur = this.mem.get(SignalProtocolStore.EPOCH_KEY);
    const next = (typeof cur === 'number' ? cur : 0) + 1;
    this.mem.set(SignalProtocolStore.EPOCH_KEY, next);
    return next;
  }

  // Drop the in-memory read cache so the next load re-reads authoritative IDB.
  // Used when a sibling tab advanced shared state and this tab must rebuild its
  // WASM client from the latest persisted bytes. No-op without IDB — there the
  // mem map is the ONLY copy of every value and must never be wiped.
  clearMemCache(): void {
    if (!this.idb) return;
    if (this._failedWrites.size === 0) {
      this.mem.clear();
      return;
    }
    // Preserve keys whose IDB write was swallowed — `mem` is their only copy, so
    // wiping them would be permanent data loss (LOW-7). Drop everything else so
    // the next load re-reads authoritative IDB.
    for (const key of this.mem.keys()) {
      if (!this._failedWrites.has(key)) this.mem.delete(key);
    }
  }

  // --- Session persistence ---
  async loadSession(
    accountId: string,
    deviceId: number,
  ): Promise<Uint8Array | undefined> {
    const bytes = await this.loadBytes(`session:${accountId}:${deviceId}`);
    return bytes ? new Uint8Array(bytes) : undefined;
  }

  async saveSession(
    accountId: string,
    deviceId: number,
    bytes: Uint8Array,
  ): Promise<void> {
    await this.saveBytes(`session:${accountId}:${deviceId}`, viewBytes(bytes));
  }

  async removeSession(accountId: string, deviceId: number): Promise<void> {
    await this.removeInternal(`session:${accountId}:${deviceId}`);
    await this.removeInternal(`session-ts:${accountId}:${deviceId}`);
  }

  async listSessionKeys(): Promise<SessionKey[]> {
    const keys = this.idb ? await this.idb.allKeys() : [...this.mem.keys()];
    const out: SessionKey[] = [];
    const seen = new Set<string>();
    for (const k of keys) {
      if (k.startsWith('session:') && !k.startsWith('session-ts:')) {
        const parts = k.split(':');
        if (parts.length === 3) {
          const uniqueKey = `${parts[1]}:${parts[2]}`;
          if (!seen.has(uniqueKey)) {
            seen.add(uniqueKey);
            out.push({
              accountId: parts[1]!,
              deviceId: parseInt(parts[2]!, 10),
            });
          }
        }
      }
    }
    return out;
  }

  async touchSessionTimestamp(
    accountId: string,
    deviceId: number,
    direction: 'send' | 'recv',
  ): Promise<void> {
    const key = `session-ts:${accountId}:${deviceId}`;
    const now = Date.now();
    const field = direction === 'send' ? 'lastSend' : 'lastRecv';
    if (this.idb) {
      try {
        // L-16: atomic read-modify-write in ONE readwrite transaction
        // (mirrors the DRH-3 epoch increment) so concurrent tabs never lose
        // each other's lastSend/lastRecv updates. Bypasses the mem cache for
        // the read — the IDB value is authoritative — then re-primes it.
        const merged = await this.idb.touchJsonTimestamp(key, field, now);
        this.mem.set(key, merged);
        return;
      } catch {
        // Fall through to the mem-cached read-modify-write on an IDB failure.
      }
    }
    const existing = await this.loadInternal<SessionTimestamp>(key);
    const ts: SessionTimestamp =
      existing &&
      typeof existing === 'object' &&
      !(existing instanceof ArrayBuffer)
        ? existing
        : { lastSend: 0, lastRecv: 0 };
    ts[field] = now;
    await this.save(key, ts);
  }

  async loadSessionTimestamp(
    accountId: string,
    deviceId: number,
  ): Promise<SessionTimestamp | undefined> {
    return this.loadInternal<SessionTimestamp>(
      `session-ts:${accountId}:${deviceId}`,
    );
  }

  // --- PreKey persistence ---
  async loadPreKey(id: number): Promise<Uint8Array | undefined> {
    const bytes = await this.loadBytes(`prekey:${id}`);
    return bytes ? new Uint8Array(bytes) : undefined;
  }

  async savePreKey(id: number, bytes: Uint8Array): Promise<void> {
    await this.saveBytes(`prekey:${id}`, viewBytes(bytes));
  }

  // --- Signed PreKey persistence ---
  async loadSignedPreKey(id: number): Promise<Uint8Array | undefined> {
    const bytes = await this.loadBytes(`signed-prekey:${id}`);
    return bytes ? new Uint8Array(bytes) : undefined;
  }

  async saveSignedPreKey(id: number, bytes: Uint8Array): Promise<void> {
    await this.saveBytes(`signed-prekey:${id}`, viewBytes(bytes));
  }

  // --- Kyber PreKey persistence ---
  async loadKyberPreKey(id: number): Promise<Uint8Array | undefined> {
    const bytes = await this.loadBytes(`kyber-prekey:${id}`);
    return bytes ? new Uint8Array(bytes) : undefined;
  }

  async saveKyberPreKey(id: number, bytes: Uint8Array): Promise<void> {
    await this.saveBytes(`kyber-prekey:${id}`, viewBytes(bytes));
  }

  // --- Sender Key persistence (for group E2EE) ---
  // Key format: sender-key:{senderAccountId}:{senderDeviceId}:{distributionId}
  async loadSenderKey(
    senderAccountId: string,
    senderDeviceId: number,
    distributionId: string,
  ): Promise<Uint8Array | undefined> {
    const bytes = await this.loadBytes(
      `sender-key:${senderAccountId}:${senderDeviceId}:${distributionId}`,
    );
    return bytes ? new Uint8Array(bytes) : undefined;
  }

  async saveSenderKey(
    senderAccountId: string,
    senderDeviceId: number,
    distributionId: string,
    bytes: Uint8Array,
  ): Promise<void> {
    await this.saveBytes(
      `sender-key:${senderAccountId}:${senderDeviceId}:${distributionId}`,
      viewBytes(bytes),
    );
  }

  async removeSenderKey(
    senderAccountId: string,
    senderDeviceId: number,
    distributionId: string,
  ): Promise<void> {
    await this.removeInternal(
      `sender-key:${senderAccountId}:${senderDeviceId}:${distributionId}`,
    );
  }

  async removeSenderKeysForGroup(distributionId: string): Promise<void> {
    const keys = this.idb ? await this.idb.allKeys() : [...this.mem.keys()];
    for (const key of keys) {
      if (key.startsWith('sender-key:') && key.endsWith(`:${distributionId}`)) {
        await this.removeInternal(key);
      }
    }
  }

  // --- Raw SKDM bytes persistence (for group decrypt restore) ---
  // Key format: skdm:{senderAccountId}:{senderDeviceId}:{distributionId}
  // Stores the decrypted SKDM protobuf bytes so process_sender_key_distribution
  // can be re-called on page reload without needing to re-decrypt the 1:1 envelope.
  async saveSkdmBytes(
    senderAccountId: string,
    senderDeviceId: number,
    distributionId: string,
    bytes: Uint8Array,
  ): Promise<void> {
    await this.saveBytes(
      `skdm:${senderAccountId}:${senderDeviceId}:${distributionId}`,
      viewBytes(bytes),
    );
  }

  async loadSkdmBytes(
    senderAccountId: string,
    senderDeviceId: number,
    distributionId: string,
  ): Promise<Uint8Array | undefined> {
    const bytes = await this.loadBytes(
      `skdm:${senderAccountId}:${senderDeviceId}:${distributionId}`,
    );
    return bytes ? new Uint8Array(bytes) : undefined;
  }

  async hasSkdmBytes(
    senderAccountId: string,
    senderDeviceId: number,
    distributionId: string,
  ): Promise<boolean> {
    const bytes = await this.loadBytes(
      `skdm:${senderAccountId}:${senderDeviceId}:${distributionId}`,
    );
    return bytes !== undefined && bytes.byteLength > 0;
  }

  async removeSkdmBytes(
    senderAccountId: string,
    senderDeviceId: number,
    distributionId: string,
  ): Promise<void> {
    await this.removeInternal(
      `skdm:${senderAccountId}:${senderDeviceId}:${distributionId}`,
    );
  }

  async removeSkdmBytesForGroup(distributionId: string): Promise<void> {
    const keys = this.idb ? await this.idb.allKeys() : [...this.mem.keys()];
    for (const key of keys) {
      if (key.startsWith('skdm:') && key.endsWith(`:${distributionId}`)) {
        await this.removeInternal(key);
      }
    }
  }

  // --- Initial SKDM bytes (iteration-0 baseline for own distributions) ---
  // Key format: skdm-init:{accountId}:{deviceId}:{distributionId}
  // Saved once when we first call create_sender_key_distribution for a distId.
  // All subsequent distributions reuse these bytes so receivers always start
  // at iteration 0 (not the current chain iteration after N messages sent).
  async saveInitialSkdmBytes(
    accountId: string,
    deviceId: number,
    distributionId: string,
    bytes: Uint8Array,
  ): Promise<void> {
    await this.saveBytes(
      `skdm-init:${accountId}:${deviceId}:${distributionId}`,
      viewBytes(bytes),
    );
  }

  async loadInitialSkdmBytes(
    accountId: string,
    deviceId: number,
    distributionId: string,
  ): Promise<Uint8Array | undefined> {
    const bytes = await this.loadBytes(
      `skdm-init:${accountId}:${deviceId}:${distributionId}`,
    );
    return bytes ? new Uint8Array(bytes) : undefined;
  }

  async removeInitialSkdmBytesForGroup(distributionId: string): Promise<void> {
    const keys = this.idb ? await this.idb.allKeys() : [...this.mem.keys()];
    for (const key of keys) {
      if (key.startsWith('skdm-init:') && key.endsWith(`:${distributionId}`)) {
        await this.removeInternal(key);
      }
    }
  }

  // --- Identity record persistence (for restore across sessions) ---
  async loadIdentityRecord(): Promise<Uint8Array | undefined> {
    const bytes = await this.loadBytes(`identity-record`);
    return bytes ? new Uint8Array(bytes) : undefined;
  }

  async saveIdentityRecord(bytes: Uint8Array): Promise<void> {
    await this.saveBytes(`identity-record`, viewBytes(bytes));
  }

  // --- Backup credentials ---
  // Stored in IndexedDB to survive tab refreshes; cleared on logout for shared computer security.
  async loadBackupCreds(): Promise<{ key?: string; salt: string } | undefined> {
    return this.loadInternal<{ key?: string; salt: string }>('backupCreds');
  }

  async storeBackupCreds(creds: { key?: string; salt: string }): Promise<void> {
    await this.save('backupCreds', creds);
  }

  async clearBackupCreds(): Promise<void> {
    await this.removeInternal('backupCreds');
  }

  // --- My device id (for multi-device fanout) ---
  async getMyDeviceId(): Promise<number | undefined> {
    return this.loadInternal<number>('myDeviceId');
  }

  async storeMyDeviceId(id: number): Promise<void> {
    await this.save('myDeviceId', id);
  }

  // --- Sent-plaintext cache ---
  // M-1: payloads are wrapped with a store-time timestamp ({__ptv:1, ts,
  // value}) so the retention sweep can age entries out. Loads unwrap
  // transparently and tolerate pre-retention legacy (unwrapped) payloads.
  private static wrapPlaintext(payload: unknown): object {
    return { __ptv: 1, ts: Date.now(), value: payload };
  }

  private static unwrapPlaintext<T>(v: unknown): T | undefined {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const o = v as Record<string, unknown>;
      if (o.__ptv === 1) return o.value as T;
    }
    return v as T; // legacy unwrapped entry
  }

  // Store-time timestamp of a plaintext-cache entry; 0 when the entry is a
  // legacy unwrapped payload (age unprovable — never TTL-evicted, sorted
  // oldest for the LRU cap).
  private static plaintextTs(v: unknown): number {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const o = v as Record<string, unknown>;
      if (o.__ptv === 1 && typeof o.ts === 'number' && Number.isFinite(o.ts))
        return o.ts;
    }
    return 0;
  }

  async loadSentPlaintext<T = unknown>(
    messageId: string,
  ): Promise<T | undefined> {
    return SignalProtocolStore.unwrapPlaintext<T>(
      await this.loadInternal(`sent:${messageId}`),
    );
  }

  async storeSentPlaintext(messageId: string, payload: unknown): Promise<void> {
    await this.save(
      `sent:${messageId}`,
      SignalProtocolStore.wrapPlaintext(payload),
    );
    await this.maybeSweepPlaintextCaches();
  }

  // --- Received-plaintext cache ---
  async loadInboundPlaintext<T = unknown>(
    messageId: string,
  ): Promise<T | undefined> {
    return SignalProtocolStore.unwrapPlaintext<T>(
      await this.loadInternal(`recv:${messageId}`),
    );
  }

  async storeInboundPlaintext(
    messageId: string,
    payload: unknown,
  ): Promise<void> {
    await this.save(
      `recv:${messageId}`,
      SignalProtocolStore.wrapPlaintext(payload),
    );
    await this.maybeSweepPlaintextCaches();
  }

  // --- Resend-replacement cache (DRH-5) ---
  // When a peer resends a message we could not decrypt (the resent copy carries
  // resend_of_id), we stash its plaintext here keyed by the FAILED message's
  // logical id (group_key ?? id). The original failed bubble then resolves from
  // this cache on the next render / page refresh, when its own ciphertext can no
  // longer be ratchet-decrypted (one-shot double ratchet). Local-only decrypted
  // content — excluded from the backup dump like sent:/recv:.
  async loadReplacementPlaintext<T = unknown>(
    failedKey: string,
  ): Promise<T | undefined> {
    return SignalProtocolStore.unwrapPlaintext<T>(
      await this.loadInternal(`replace:${failedKey}`),
    );
  }

  async storeReplacementPlaintext(
    failedKey: string,
    payload: unknown,
  ): Promise<void> {
    await this.save(
      `replace:${failedKey}`,
      SignalProtocolStore.wrapPlaintext(payload),
    );
    await this.maybeSweepPlaintextCaches();
  }

  // M-1: evict plaintext-cache entries (sent:/recv:/replace:) and stale
  // sibling-backfill outbox entries provably older than the TTL, then enforce
  // the global LRU cap (oldest store-time first) across the three plaintext
  // prefixes. Only entries past the TTL or oldest beyond the cap are removed —
  // recent entries are never touched, so messages read by id for rendering
  // keep resolving. Safe to call any time (the crypto cleanup pass calls it
  // explicitly); it also runs lazily on plaintext-cache writes, at most once
  // per PLAINTEXT_SWEEP_INTERVAL_MS. `limits` overrides exist for tests.
  // Returns the number of evicted entries.
  async sweepPlaintextCaches(
    now: number = Date.now(),
    limits?: { ttlMs?: number; maxEntries?: number },
  ): Promise<number> {
    const ttlMs = limits?.ttlMs ?? SignalProtocolStore.PLAINTEXT_TTL_MS;
    const maxEntries =
      limits?.maxEntries ?? SignalProtocolStore.MAX_PLAINTEXT_ENTRIES;
    const keys = this.idb ? await this.idb.allKeys() : [...this.mem.keys()];
    const plaintext: { key: string; ts: number }[] = [];
    const backfill: { key: string; ts: number }[] = [];
    for (const key of keys) {
      if (
        key.startsWith('sent:') ||
        key.startsWith('recv:') ||
        key.startsWith('replace:')
      ) {
        const v = await this.loadInternal(key);
        plaintext.push({ key, ts: SignalProtocolStore.plaintextTs(v) });
      } else if (key.startsWith('sibling-backfill:')) {
        const v = await this.loadInternal<SiblingBackfillEntry>(key);
        const ts = v && typeof v.createdAt === 'number' ? v.createdAt : 0;
        backfill.push({ key, ts });
      }
    }
    const evict = new Set<string>();
    for (const e of [...plaintext, ...backfill]) {
      // TTL: only entries with a provable store-time older than the TTL.
      if (e.ts > 0 && now - e.ts > ttlMs) evict.add(e.key);
    }
    // LRU cap across sent:/recv:/replace: — oldest store-time first (legacy
    // unstamped entries sort oldest).
    const survivors = plaintext
      .filter((e) => !evict.has(e.key))
      .sort((a, b) => a.ts - b.ts);
    const excess = survivors.length - maxEntries;
    for (let i = 0; i < excess; i++) evict.add(survivors[i]!.key);
    for (const key of evict) await this.removeInternal(key);
    await this.save(SignalProtocolStore.SWEEP_MARKER_KEY, now);
    return evict.size;
  }

  // Lazy retention trigger on plaintext-cache writes: runs the sweep at most
  // once per PLAINTEXT_SWEEP_INTERVAL_MS (persisted marker, so the interval
  // holds across reloads and tabs). Sweep failures never break the write.
  private async maybeSweepPlaintextCaches(): Promise<void> {
    try {
      const last = await this.loadInternal<number>(
        SignalProtocolStore.SWEEP_MARKER_KEY,
      );
      const now = Date.now();
      if (
        typeof last === 'number' &&
        now - last < SignalProtocolStore.PLAINTEXT_SWEEP_INTERVAL_MS
      )
        return;
      await this.sweepPlaintextCaches(now);
    } catch {
      // Non-fatal: retention on the write path is best-effort; the crypto
      // cleanup pass calls sweepPlaintextCaches() explicitly.
    }
  }

  // --- DRH-4 per-conversation sequence state ---
  // Local-only convergence metadata (message IDS only — no plaintext/keys, per
  // INV-DRH4-2). Two keys per peer:
  //   seq:ack:<peer>  — the highest contiguously-decrypted id the PEER has
  //                     ack'd to us (sender uses it to pick proactive resends).
  //   seq:sent:<peer> — the last high-water we ourselves ACK'd to the peer
  //                     (recipient uses it to throttle duplicate acks).
  // Excluded from the backup dump() (like sent:/recv:/replace:) and wiped by
  // destroyAll — the state is cheap to rebuild from the thread after a restore.
  async loadPeerAck(peerAccountId: string): Promise<string | undefined> {
    return this.loadInternal<string>(`seq:ack:${peerAccountId}`);
  }

  async storePeerAck(peerAccountId: string, ackId: string): Promise<void> {
    await this.save(`seq:ack:${peerAccountId}`, ackId);
  }

  async loadSentAck(peerAccountId: string): Promise<string | undefined> {
    return this.loadInternal<string>(`seq:sent:${peerAccountId}`);
  }

  async storeSentAck(peerAccountId: string, ackId: string): Promise<void> {
    await this.save(`seq:sent:${peerAccountId}`, ackId);
  }

  // --- Remote identity key tracking (to detect regeneration) ---
  // Keyed per (account, device): different devices of the same account have
  // different identity keys, so a per-account key collides and produces false
  // "identity changed" detections (forcing needless session re-establishment).
  // Backward compatible: the legacy per-account key written by the single-device
  // era is treated as device 1's key and read as a fallback.
  async loadRemoteIdentityKey(
    accountId: string,
    deviceId?: number,
  ): Promise<string | undefined> {
    if (deviceId === undefined) {
      return this.loadInternal<string>(`remote-id-key:${accountId}`);
    }
    const perDevice = await this.loadInternal<string>(
      `remote-id-key:${accountId}:${deviceId}`,
    );
    if (perDevice !== undefined) return perDevice;
    // Legacy fallback: the single-device era stored device 1 without a suffix.
    if (deviceId === 1) {
      return this.loadInternal<string>(`remote-id-key:${accountId}`);
    }
    return undefined;
  }

  async saveRemoteIdentityKey(
    accountId: string,
    b64Key: string,
    deviceId?: number,
  ): Promise<void> {
    const key =
      deviceId === undefined
        ? `remote-id-key:${accountId}`
        : `remote-id-key:${accountId}:${deviceId}`;
    await this.save(key, b64Key);
  }

  // --- Safety number verification state (H-4) ---
  // Keyed per (contactAccountId, deviceId) — a contact's devices have distinct
  // identity keys, so a per-account flag both collides across devices and let
  // a verification survive a key change. The flag is stored TOGETHER with the
  // identity-key fingerprint that was actually compared, and is only reported
  // verified when the stored key matches the currently-presented key
  // (isSafetyNumberVerifiedForKey). Back-compat: deviceId defaults to 1; the
  // legacy per-account boolean entry (single-device era, written as
  // `safety-verified:<accountId>`) is read as device 1's slot but — carrying
  // no bound key — always reports unverified (safest).
  async saveSafetyNumberVerified(
    accountId: string,
    identityKey: string | boolean,
    deviceId = 1,
  ): Promise<void> {
    if (typeof identityKey !== 'string') {
      // DEPRECATED shim for the pre-H-4 boolean caller
      // saveSafetyNumberVerified(accountId, true). A keyless "verified" flag
      // proves nothing, so it is persisted in a form that always reports
      // UNVERIFIED under the new scheme. Remove once all callers pass the
      // compared identity key.
      await this.save(`safety-verified:${accountId}:${deviceId}`, {
        verified: false,
      });
      return;
    }
    await this.save(`safety-verified:${accountId}:${deviceId}`, {
      verified: true,
      key: identityKey,
    });
  }

  async loadSafetyNumberVerified(
    accountId: string,
    deviceId = 1,
  ): Promise<{ verified: boolean; key?: string }> {
    const parsed = SignalProtocolStore.parseSafetyNumberEntry(
      await this.loadInternal(`safety-verified:${accountId}:${deviceId}`),
    );
    if (parsed) return parsed;
    // Legacy fallback: the single-device era stored device 1 without a suffix.
    if (deviceId === 1) {
      const parsedLegacy = SignalProtocolStore.parseSafetyNumberEntry(
        await this.loadInternal(`safety-verified:${accountId}`),
      );
      if (parsedLegacy) return parsedLegacy;
    }
    return { verified: false };
  }

  private static parseSafetyNumberEntry(
    raw: unknown,
  ): { verified: boolean; key?: string } | undefined {
    if (raw === undefined) return undefined;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      // Legacy bare boolean (or anything without a bound key): safest is to
      // treat it as unverified — there is no recorded key to compare against.
      return { verified: false };
    }
    const o = raw as Record<string, unknown>;
    const key = typeof o.key === 'string' ? o.key : undefined;
    // verified is only meaningful together with the bound identity key.
    return {
      verified: o.verified === true && key !== undefined,
      ...(key !== undefined ? { key } : {}),
    };
  }

  // True only when this (contact, device) was verified AND the identity key
  // that was verified matches the key currently presented. Any key change
  // (reinstall, newly linked device, or MITM) flips this to false until the
  // user re-verifies.
  async isSafetyNumberVerifiedForKey(
    accountId: string,
    deviceId: number,
    currentKey: string,
  ): Promise<boolean> {
    const s = await this.loadSafetyNumberVerified(accountId, deviceId);
    return s.verified && s.key !== undefined && s.key === currentKey;
  }

  // Reset one device (deviceId given; device 1 also clears the legacy
  // unsuffixed slot) or every device of the contact (deviceId omitted).
  async resetSafetyNumberVerified(
    accountId: string,
    deviceId?: number,
  ): Promise<void> {
    if (deviceId !== undefined) {
      await this.remove(`safety-verified:${accountId}:${deviceId}`);
      if (deviceId === 1) await this.remove(`safety-verified:${accountId}`);
      return;
    }
    const keys = this.idb ? await this.idb.allKeys() : [...this.mem.keys()];
    for (const key of keys) {
      if (key.startsWith(`safety-verified:${accountId}`)) {
        await this.removeInternal(key);
      }
    }
  }

  // --- M-2/M-6b: send-side group distribution-id history ---
  // Append-only per-group history of every distribution_id the server has
  // served for this group (cap ~20, oldest dropped), plus a GLOBAL
  // distId → groupChatId owner map. A distId the server served BEFORE for
  // this group and then serves AGAIN after a newer one is a rollback
  // (removal-rollback attack: old-epoch key material may still be live, and
  // the removed member kept the iteration-0 chain seed). A distId served
  // for a DIFFERENT group is a cross-group collision (two groups would
  // share sender-key chain material). Both must refuse to send.
  private static readonly GROUP_DIST_ID_HISTORY_CAP = 20;

  async loadGroupDistIdHistory(groupChatId: string): Promise<string[]> {
    const v = await this.loadInternal<string[]>(
      `group-dist-history:${groupChatId}`,
    );
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [];
  }

  // Record a server-served distId for a group.
  //   'new'      — never seen for this group (recorded; caller may proceed)
  //   'seen'     — the group's CURRENT (latest recorded) distId
  //   'rollback' — served before for THIS group but superseded since, OR the
  //                same distId is owned by a DIFFERENT group (collision).
  //                The caller must REFUSE to send/distribute under it.
  async recordGroupDistId(
    groupChatId: string,
    distId: string,
  ): Promise<'new' | 'seen' | 'rollback'> {
    const ownerKey = `group-dist-owner:${distId}`;
    const owner = await this.loadInternal<string>(ownerKey);
    if (owner !== undefined && owner !== groupChatId) {
      // Cross-group collision — refuse (reported as 'rollback' so every
      // caller treats any non-new/seen result as refusal).
      return 'rollback';
    }
    const key = `group-dist-history:${groupChatId}`;
    const hist = await this.loadGroupDistIdHistory(groupChatId);
    const last = hist[hist.length - 1];
    if (last === distId) return 'seen';
    if (hist.includes(distId)) return 'rollback';
    const next = [...hist, distId].slice(
      -SignalProtocolStore.GROUP_DIST_ID_HISTORY_CAP,
    );
    await this.save(key, next);
    if (owner === undefined) await this.save(ownerKey, groupChatId);
    return 'new';
  }

  // Global distId → groupChatId owner lookup (cross-group collision detection).
  async loadGroupDistIdOwner(distId: string): Promise<string | undefined> {
    return this.loadInternal<string>(`group-dist-owner:${distId}`);
  }

  // --- H-5: last-seen device-id set per contact ---
  // Updated on FIRST contact (TOFU: the send path records whatever devices
  // the server then served) and on explicit user acknowledgment of a new
  // device. A device id that appears later for an already-known contact is
  // a trust event ("contact added a new device") and is gated out of the
  // fanout until acknowledged.
  async loadSeenDeviceIds(accountId: string): Promise<number[]> {
    const v = await this.loadInternal<number[]>(`seen-devices:${accountId}`);
    return Array.isArray(v)
      ? v.filter((x) => typeof x === 'number' && Number.isInteger(x))
      : [];
  }

  async saveSeenDeviceIds(
    accountId: string,
    deviceIds: number[],
  ): Promise<void> {
    const uniq = Array.from(new Set(deviceIds)).sort((a, b) => a - b);
    await this.save(`seen-devices:${accountId}`, uniq);
  }

  // --- H-3: one-shot identity-change acknowledgments ---
  // When the user verifies/acknowledges a key change (or a new device) for
  // (accountId, deviceId), an entry bound to the acknowledged identity key
  // is stored here. The send path turns it into a per-target
  // allowIdentityKeyChange allowance and CONSUMES it (deletes) after the
  // first successful send, so a stale ack can never auto-approve a later,
  // different key. Crypto additionally honors an allowance entry only when
  // its bound key matches the currently-served one.
  async saveIdentityChangeAck(
    accountId: string,
    deviceId: number,
    identityKey: string,
  ): Promise<void> {
    await this.save(`identity-ack:${accountId}:${deviceId}`, {
      key: identityKey,
      at: Date.now(),
    });
  }

  async loadIdentityChangeAcks(
    accountId: string,
  ): Promise<Array<{ deviceId: number; key: string }>> {
    const keys = this.idb ? await this.idb.allKeys() : [...this.mem.keys()];
    const out: Array<{ deviceId: number; key: string }> = [];
    const prefix = `identity-ack:${accountId}:`;
    for (const k of keys) {
      if (!k.startsWith(prefix)) continue;
      const deviceId = parseInt(k.slice(prefix.length), 10);
      if (!Number.isInteger(deviceId)) continue;
      const v = await this.loadInternal<{ key?: unknown }>(k);
      if (v && typeof v === 'object' && typeof v.key === 'string') {
        out.push({ deviceId, key: v.key });
      }
    }
    return out;
  }

  async removeIdentityChangeAck(
    accountId: string,
    deviceId: number,
  ): Promise<void> {
    await this.removeInternal(`identity-ack:${accountId}:${deviceId}`);
  }

  // --- MD-h: own-sibling backfill outbox ---
  // Durable queue of message copies a sibling device MISSED during fan-out (its
  // session was throttled/unusable at send time so encryptMultiDevice skipped
  // it). A later flush re-encrypts + re-delivers the copy to that sibling, so a
  // sender's own linked device isn't permanently missing its own sent message.
  // Key: sibling-backfill:<recipientAccountId>:<deviceId> — one pending copy per
  // (thread, sibling); a newer send for the same pair overwrites the older
  // pending copy (the sibling only needs the latest, and both are the caller's
  // own outbound messages). Excluded from dump() and wiped by destroyAll.
  // Bounded to MAX_SIBLING_BACKFILL (oldest dropped) so a persistently-
  // unreachable sibling can't grow the queue without limit.
  async saveSiblingBackfill(entry: SiblingBackfillEntry): Promise<void> {
    await this.save(
      `sibling-backfill:${entry.recipientAccountId}:${entry.deviceId}`,
      entry,
    );
    await this.enforceSiblingBackfillCap();
    await this.maybeSweepPlaintextCaches();
  }

  async loadSiblingBackfills(): Promise<SiblingBackfillEntry[]> {
    const keys = this.idb ? await this.idb.allKeys() : [...this.mem.keys()];
    const out: SiblingBackfillEntry[] = [];
    for (const key of keys) {
      if (!key.startsWith('sibling-backfill:')) continue;
      const v = await this.loadInternal<SiblingBackfillEntry>(key);
      if (v) out.push(v);
    }
    return out;
  }

  async removeSiblingBackfill(
    recipientAccountId: string,
    deviceId: number,
  ): Promise<void> {
    await this.removeInternal(
      `sibling-backfill:${recipientAccountId}:${deviceId}`,
    );
  }

  private async enforceSiblingBackfillCap(): Promise<void> {
    const all = await this.loadSiblingBackfills();
    if (all.length <= SignalProtocolStore.MAX_SIBLING_BACKFILL) return;
    all.sort((a, b) => a.createdAt - b.createdAt);
    const drop = all.slice(
      0,
      all.length - SignalProtocolStore.MAX_SIBLING_BACKFILL,
    );
    for (const e of drop)
      await this.removeSiblingBackfill(e.recipientAccountId, e.deviceId);
  }

  // --- Dump and hydrate for backup/restore ---
  async dump(): Promise<Record<string, unknown>> {
    const keys = this.idb ? await this.idb.allKeys() : [...this.mem.keys()];
    const out: Record<string, unknown> = {};
    for (const key of keys) {
      // Never include backup credentials in the dump — they would create a
      // circular self-reference (the backup ciphertext encrypted with the key
      // that is itself inside the backup).
      if (key === 'backupCreds') continue;
      // Plaintext cache entries (sent:* / recv:*) are local-only decrypted
      // message text. Including them in backups would mean the server holds
      // (encrypted) message content, bloating backup size unboundedly and
      // undermining the purpose of E2EE backups (which need only identity +
      // session + prekey state to restore the ability to encrypt/decrypt).
      if (
        key.startsWith('sent:') ||
        key.startsWith('recv:') ||
        key.startsWith('replace:') ||
        key.startsWith('seq:') || // DRH-4 local convergence metadata (ids only)
        key.startsWith('sibling-backfill:') || // MD-h delivery state (plaintext; stale group_keys after restore)
        key === SignalProtocolStore.EPOCH_KEY || // DRH-3 cross-tab epoch (local-only)
        key === 'restored-at' || // restore cutoff — local-only; each restore re-derives it from the backup's updated_at
        key === SignalProtocolStore.SWEEP_MARKER_KEY // M-1 retention sweep marker (local-only)
      )
        continue;
      // NOTE (M-5): group chain state (sender-key:* / skdm:* / skdm-init:*)
      // is intentionally NOT excluded here. dump() has TWO consumers: the
      // backup upload AND crypto.ts restoreFromStore, which re-imports sender
      // keys into a rebuilt WASM client after every epoch-triggered reload —
      // excluding them here breaks group crypto on any client rebuild. The
      // M-5 restore-rollback protection lives in hydrate() instead (see
      // isGroupChainKey below): a backup RESTORE never persists group chain
      // state, so the restored device starts group crypto fresh.
      const v = this.mem.get(key);
      if (v !== undefined) {
        out[key] = v instanceof ArrayBuffer ? encodeArrayBuffer(v) : v;
      } else if (this.idb) {
        const raw = await this.idb.get(key);
        if (raw !== undefined) {
          out[key] = raw instanceof ArrayBuffer ? encodeArrayBuffer(raw) : raw;
        }
      }
    }
    return out;
  }

  // M-12: hydrate + epoch bump are ONE logical unit (guarded by this class —
  // callers cannot restore keys without publishing the mutation). The bump
  // makes sibling tabs' syncClientToEpoch see the advance and reload-before-op
  // instead of clobbering the freshly hydrated state with a stale in-memory
  // client (the DRH-3 CAS adoption rule in crypto.ts withWriterLock: a tab
  // whose clientEpoch is behind rebuilds from IDB before its next op).
  // Concurrent hydrates are serialized so each bump follows the writes of its
  // OWN restore.
  private _hydrateChain: Promise<void> = Promise.resolve();

  // M-5: group chain state keys. hydrate() skips these so a backup RESTORE
  // never persists a sender chain that is BEHIND what the group already
  // consumed (a restored device's next group send would re-emit an
  // already-used iteration → AES-CBC key+IV reuse + DuplicatedMessage loss).
  // With them filtered at restore time, the restored device starts group
  // crypto fresh and re-joins automatically: fetchAndProcessSenderKeys
  // processes any SKDM row it has no local skdm: bytes for (SKDM rows persist
  // server-side until rotation), and createSenderKeyDistribution mints a fresh
  // iteration-0 chain + redistributes on the first send/poll when no
  // skdm-init: entry exists.
  private static isGroupChainKey(key: string): boolean {
    return (
      key.startsWith('sender-key:') ||
      key.startsWith('skdm:') ||
      key.startsWith('skdm-init:')
    );
  }

  async hydrate(data: Record<string, unknown>): Promise<void> {
    const run = this._hydrateChain.then(async () => {
      for (const [k, v] of Object.entries(data)) {
        // M-5: never restore group chain state (see isGroupChainKey).
        if (SignalProtocolStore.isGroupChainKey(k)) continue;
        const decoded =
          v && typeof v === 'object' && '__bin' in v ? decodeBinaryValue(v) : v;
        await this.save(k, decoded);
      }
      // Publish the restore via the atomic epoch increment (skipped for an
      // empty restore — nothing mutated).
      if (Object.keys(data).length > 0) await this.bumpEpoch();
    });
    this._hydrateChain = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  /**
   * Wipe ALL state: every key in both IndexedDB and in-memory cache,
   * including plaintext cache (sent/recv) and backupCreds. Used by
   * E2eeCrypto.destroy() on logout. Unlike dump(), this does not skip
   * any keys.
   */
  async destroyAll(): Promise<void> {
    const keys = this.idb ? await this.idb.allKeys() : [...this.mem.keys()];
    for (const key of keys) {
      await this.removeInternal(key);
    }
    await this.clearBackupCreds();
    this.mem.clear();
  }
}

// --- Binary codec ---
function encodeArrayBuffer(buf: ArrayBuffer): { __bin: string } {
  const u8 = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < u8.length; i += 0x8000) {
    s += String.fromCharCode.apply(
      null,
      u8.subarray(i, i + 0x8000) as unknown as number[],
    );
  }
  return { __bin: btoa(s) };
}

function encodeBinaryValues(v: unknown): unknown {
  if (v instanceof ArrayBuffer) {
    return encodeArrayBuffer(v);
  }
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      out[k] = encodeBinaryValues(val);
    }
    return out;
  }
  return v;
}

function decodeBinaryValue(v: unknown): unknown {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const obj = v as Record<string, unknown>;
    if (typeof obj.__bin === 'string' && Object.keys(obj).length === 1) {
      const bin = atob(obj.__bin);
      const out = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
      return out.buffer;
    }
  }
  return v;
}

function decodeBinaryValues(v: unknown): unknown {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const obj = v as Record<string, unknown>;
    if (typeof obj.__bin === 'string' && Object.keys(obj).length === 1) {
      return decodeBinaryValue(v);
    }
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(obj)) {
      out[k] = decodeBinaryValues(val);
    }
    return out;
  }
  return v;
}
